import React from 'react'
import { format } from 'date-fns'
import { connect } from 'react-redux'
import { DragDropContext } from 'react-beautiful-dnd'
import {
  DragDropTable,
  PopupProvider,
  ListButton,
  MoreButton,
  RoundShadowButton,
  AddToPlaylistTransformer
} from 'components'
import { playNextLogo, playAtEndLogo } from 'images'
import { PlayListService } from 'services'
import PlaylistActions from 'reducers/PlayList'
import NotificationActions from 'reducers/Notification'
import ReleaseActions, { thunks as ReleaseThunks } from 'reducers/Releases'
import PropTypes from 'prop-types'
import PlayQueueActions, { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import { thunks as DownloadThunks } from 'reducers/Downloads'
import { withTranslation } from 'react-i18next'
import {
  DATE_FORMAT,
  getStringFromSeconds,
  formatDateWithTimeZoneFromSeconds
} from 'utils/dateHelpers'
import stringHelpers from 'utils/stringHelpers'
import cloneDeep from 'lodash/cloneDeep'
import { arrayHelpers } from 'utils'
import { browserHelpers } from 'utils'
import placeholderLogo from 'images/icons/placeholder.png'
import {
  AnchorPositions,
  PopupDirections,
  SongTableColumns,
  NotificationType
} from 'constants/enums'
import { PLAY_TRIGGERS } from 'constants/tracking'
import formatters from './Formatters'

const mapStateToProps = ({ global, options }) => ({
  markets: options.markets,
  isIpadView: global.isIpadView
})

@connect(mapStateToProps)
class DragDropSongsTable extends React.Component {
  static propTypes = {
    showMarkets: PropTypes.bool,
    controlledHover: PropTypes.bool,
    showPlayQueueOptions: PropTypes.bool,
    columns: PropTypes.arrayOf(PropTypes.string),
    playlist: PropTypes.object,
    items: PropTypes.arrayOf(PropTypes.object),
    markets: PropTypes.arrayOf(PropTypes.object),
    playSource: PropTypes.string,

    t: PropTypes.func,
    renderModal: PropTypes.func,
    onHoverStop: PropTypes.func,
    dispatch: PropTypes.func.isRequired,
    isIpadView: PropTypes.bool
  }

  static defaultProps = {
    controlledHover: false
  }

  handleShowModal = (song) => {
    const { dispatch } = this.props
    dispatch(
      ReleaseThunks.getDetail(song.releaseid, (releaseDetail) => {
        dispatch(ReleaseActions.toggleReleaseInModal(releaseDetail))
      })
    )
  }

  handlePlaySong = (song) => {
    const { dispatch, playSource } = this.props
    dispatch(
      PlayQueueThunks.playSong(song, {
        source: playSource,
        trigger: PLAY_TRIGGERS.ROW
      })
    )
  }

  handleDownloadSong = (song) => {
    const { dispatch } = this.props
    dispatch(
      DownloadThunks.addSongToDownloadQueue(song, {
        source: 'drag_drop_songs_table'
      })
    )
  }

  handlePlayNext = (song) => {
    const { dispatch } = this.props
    dispatch(PlayQueueActions.insertItemNext(song))
  }

  handlePlayAtTheBottom = (song) => {
    const { dispatch } = this.props
    dispatch(PlayQueueActions.insertItemAtBottom(song))
  }

  insertIds = () => {
    const { items } = this.props
    this._itemsWithIds = items.map((item, i) => ({
      ...item,
      id: item.trackid,
      index: i + 1 + '.'
    }))
  }

  getColumns = () => {
    const { t, markets, showMarkets, columns, isIpadView } = this.props
    const { limitLength } = stringHelpers
    const flexClasses = 'flex-shrink-0'
    const options = [
      {
        name: SongTableColumns.index,
        title: '',
        // width: 70,
        field: 'index',
        cellClassName: 'text-left',
        cellStyleName: 'release-index'
      },
      {
        name: SongTableColumns.coverart,
        image: true,
        src: 'coverart',
        cellStyleName: 'release-coverart',
        fallback: placeholderLogo
      },
      {
        title: '',
        field: 'market_tags',
        cellClassName: 'flex-none',
        cellStyleName: 'release-market',
        format: (field) => formatters.formatMarketsCell(markets, field)
      },
      {
        name: SongTableColumns.artist,
        title: t('release-list.artist'),
        sort: 'artist',
        field: 'artist',
        resizable: true,
        cellClassName: `truncate ${flexClasses}`,
        cellStyleName: 'release-artist'
      },
      {
        name: SongTableColumns.title,
        title: t('release-list.title'),
        sort: 'title',
        field: 'title',
        resizable: true,
        cellClassName: `truncate ${flexClasses}`,
        cellStyleName: 'release-title'
      },
      {
        name: SongTableColumns.duration,
        title: t('release-list.duration'),
        sort: 'duration',
        field: 'duration',
        resizable: true,
        cellClassName: `${flexClasses}`,
        cellStyleName: 'release-duration',
        format: (field) => getStringFromSeconds(field)
      },

      {
        name: SongTableColumns.labels,
        title: t('release-list.label'),
        field: 'labels',
        cellClassName: `break-words ${flexClasses}`,
        cellStyleName: 'release-label',
        resizable: true,
        format: (field) => formatters.formatLabelCell(field)
      },
      {
        name: SongTableColumns.genres,
        title: t('release-list.formats'),
        field: 'genres',
        cellClassName: `break-words ${flexClasses}`,
        cellStyleName: 'release-format',
        resizable: true,
        format: (field) => limitLength(field.join(', '), 30)
      },
      {
        name: SongTableColumns.availableDate,
        title: t('grid-list.available-date'),
        sort: 'available',
        field: 'available',
        cellClassName: `truncate ${flexClasses}`,
        cellStyleName: 'release-date',
        resizable: true,
        format: (field) => format(new Date(field), DATE_FORMAT)
      },
      {
        name: SongTableColumns.impactDate,
        title: t('grid-list.impact-date'),
        sort: 'impactdate',
        field: 'impactdate',
        cellClassName: `truncate ${flexClasses}`,
        cellStyleName: 'release-date',
        resizable: true,
        format: (field, row) => {
          if (field) {
            return formatDateWithTimeZoneFromSeconds(field)
          }
          const formattedAvailableDate = format(
            new Date(row?.available),
            DATE_FORMAT
          )

          return formattedAvailableDate
        }
      }
    ]

    const processedOptions = options.filter(
      (item) => showMarkets || item.name !== SongTableColumns.markets
    )

    if (columns === undefined) {
      return processedOptions
    } else {
      return options.filter((item) => columns.includes(item.name))
    }
  }

  renderModal = (song) => {
    const { t, showPlayQueueOptions } = this.props
    const { candownload } = song

    return (
      <PopupProvider
        tapOutSideToDismiss
        anchorPosition={AnchorPositions.bottomLeft}
        popupDirection={PopupDirections.downLeft}
        renderTrigger={({ setOnOff }) => (
          <MoreButton
            onClick={(event) => {
              event.stopPropagation()
              setOnOff(true)
            }}
          />
        )}
        renderChildren={({ setOnOff }) => (
          <div className="bg-white rounded text-black overflow-y-auto max-h-64 scrollbar-blue w-40">
            <AddToPlaylistTransformer
              showPlayQueueOptions={showPlayQueueOptions}
              song={song}
              setOnOff={setOnOff}
              previousOptions={
                <>
                  <ListButton
                    render={() => {
                      return (
                        <div className="flex flex-row items-center justify-start w-full">
                          <RoundShadowButton
                            removeTextComponent
                            icon={playNextLogo}
                            cicleWidthClass="w-4"
                            imageWidthClass="w-2"
                          />
                          <p className="text-xs px-2">
                            {t('general.play-next')}
                          </p>
                        </div>
                      )
                    }}
                    onClick={() => this.handlePlayNext(song)}
                  />
                  <ListButton
                    render={() => {
                      return (
                        <div className="flex flex-row items-center justify-start w-full">
                          <RoundShadowButton
                            removeTextComponent
                            icon={playAtEndLogo}
                            cicleWidthClass="w-4"
                            imageWidthClass="w-2"
                          />
                          <p className="text-xs px-2">
                            {t('general.play-later')}
                          </p>
                        </div>
                      )
                    }}
                    onClick={() => this.handlePlayAtTheBottom(song)}
                  />
                  {candownload && browserHelpers.browserAllowsDownload && (
                    <ListButton
                      name={t('general.download')}
                      onClick={() => this.handleDownloadSong(song)}
                    />
                  )}
                </>
              }
            />
          </div>
        )}
      />
    )
  }

  handleOnDragEnd = (result) => {
    const { dispatch } = this.props
    // If dragged item did not change position, do nothing
    if (!result.destination) {
      return
    }
    // else, clone local copy of playlist.songs
    let playlistClone = cloneDeep(this.props.playlist)
    // reorder songs
    let reorderedSongs = arrayHelpers.reorder(
      playlistClone.songs,
      result.source.index,
      result.destination.index
    )
    // update UI - setPlaylistUnderDisplay to new reordered list of songs
    playlistClone.songs = reorderedSongs
    dispatch(PlaylistActions.setPlaylistUnderDisplay(playlistClone))
    // get id of track before/after dragged track
    let newIndex = result.destination.index
    let trackid = result.draggableId
    // if a track is moved to index 0 (first item of playlist), reference the next track and set position to 'before',
    // else reference previous track and set position to 'after'
    let referenceTrackid =
      newIndex == 0
        ? playlistClone.songs[newIndex + 1].trackid
        : playlistClone.songs[newIndex - 1].trackid
    let position = newIndex == 0 ? 'before' : 'after'
    // call api to update song position
    PlayListService.updateTrackPosition({
      playlistid: this.props.playlist.playlistid,
      trackid: parseInt(trackid),
      position: position,
      referenceTrackid: referenceTrackid
    }).catch((error) => {
      // if response has error from server, undo reordering of songs in UI
      let originalSongs = arrayHelpers.reorder(
        playlistClone.songs,
        result.destination.index,
        result.source.index
      )
      playlistClone.songs = originalSongs
      dispatch(PlaylistActions.setPlaylistUnderDisplay(playlistClone))
      // display notification to alert user of error
      dispatch(
        NotificationActions.broadcastMessage(
          error.data.error,
          NotificationType.hasError
        )
      )
    })
  }

  render() {
    this.insertIds()
    const { renderModal, onHoverStop, controlledHover } = this.props
    const items = this._itemsWithIds

    return (
      <div>
        <DragDropContext onDragEnd={this.handleOnDragEnd}>
          <DragDropTable
            items={items}
            controlledHover={controlledHover}
            tableId="songs"
            // columns={showMarkets ? columns : columns.slice(0, -1)}
            columns={this.getColumns()}
            renderModal={renderModal || this.renderModal}
            onHoverStop={onHoverStop}
            onClick={this.handleShowModal}
            onPlay={this.handlePlaySong}
          />
        </DragDropContext>
      </div>
    )
  }
}

export default withTranslation()(DragDropSongsTable)
